import { useState, useEffect } from 'react';
import CurrencyInput from '@/components/base/CurrencyInput';
import { capitalizeWords, digitsOnly } from '@/lib/inputHelpers';

export interface EmployeeFormData {
  full_name: string;
  phone: string;
  email: string;
  identity_no: string;
  address: string;
  position: string;
  department: string;
  hire_date: string;
  salary: number;
  salary_payment_day: number;
  status: string;
  emergency_contact_name: string;
  emergency_contact_phone: string;
  notes: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: EmployeeFormData) => void;
  initialData?: any;
}

const POSITIONS = ['Yönetici', 'Satış Personeli', 'Kasa', 'Muhasebe', 'Depo Personeli', 'Teslimat Personeli', 'Montaj Personeli', 'Servis Personeli'];
const DEPARTMENTS = ['Yönetim', 'Satış', 'Finans', 'Depo', 'Teslimat', 'Montaj', 'Servis', 'Muhasebe'];
const STATUSES = ['Aktif', 'İzinli', 'Raporlu', 'İşten Ayrıldı', 'Askıda'];
const PAYMENT_DAYS = [1, 5, 10, 15, 20, 25, 28];

export default function EmployeeFormModal({ isOpen, onClose, onSave, initialData }: Props) {
  const [form, setForm] = useState<EmployeeFormData>({
    full_name: '',
    phone: '',
    email: '',
    identity_no: '',
    address: '',
    position: 'Satış Personeli',
    department: 'Satış',
    hire_date: new Date().toISOString().split('T')[0],
    salary: 0,
    salary_payment_day: 1,
    status: 'Aktif',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    notes: '',
  });

  useEffect(() => {
    if (initialData) {
      setForm({
        full_name: initialData.full_name || '',
        phone: initialData.phone || '',
        email: initialData.email || '',
        identity_no: initialData.identity_no || '',
        address: initialData.address || '',
        position: initialData.position || 'Satış Personeli',
        department: initialData.department || 'Satış',
        hire_date: initialData.hire_date || new Date().toISOString().split('T')[0],
        salary: parseFloat(initialData.salary) || 0,
        salary_payment_day: initialData.salary_payment_day || 1,
        status: initialData.status || 'Aktif',
        emergency_contact_name: initialData.emergency_contact_name || '',
        emergency_contact_phone: initialData.emergency_contact_phone || '',
        notes: initialData.notes || '',
      });
    }
  }, [initialData, isOpen]);

  if (!isOpen) return null;

  const update = (field: keyof EmployeeFormData, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.full_name.trim()) return;
    onSave(form);
  };

  const inputClass = 'w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-400/20 text-foreground-800 placeholder:text-foreground-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-background-100';
  const selectClass = `${inputClass} cursor-pointer`;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-background-200/70">
          <h3 className="text-lg font-semibold text-foreground-900">
            {initialData ? 'Personel Düzenle' : 'Yeni Personel Ekle'}
          </h3>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 cursor-pointer">
            <i className="ri-close-line text-lg text-foreground-500"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Ad Soyad <span className="text-red-400">*</span></label>
              <input type="text" value={form.full_name} onChange={e => update('full_name', capitalizeWords(e.target.value))} className={inputClass} placeholder="Ad Soyad" required />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Telefon</label>
              <input type="tel" value={form.phone} onChange={e => update('phone', digitsOnly(e.target.value))} className={inputClass} placeholder="05XX XXX XX XX" />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">E-posta</label>
              <input type="email" value={form.email} onChange={e => update('email', e.target.value)} className={inputClass} placeholder="ornek@mail.com" />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">TC Kimlik No</label>
              <input type="text" value={form.identity_no} onChange={e => update('identity_no', e.target.value)} className={inputClass} placeholder="11 haneli" maxLength={11} />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Adres</label>
              <input type="text" value={form.address} onChange={e => update('address', e.target.value)} className={inputClass} placeholder="Açık adres" />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Pozisyon</label>
              <select value={form.position} onChange={e => update('position', e.target.value)} className={selectClass}>
                {POSITIONS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Departman</label>
              <select value={form.department} onChange={e => update('department', e.target.value)} className={selectClass}>
                {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">İşe Giriş Tarihi</label>
              <input type="date" value={form.hire_date} onChange={e => update('hire_date', e.target.value)} className={inputClass} />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Çalışma Durumu</label>
              <select value={form.status} onChange={e => update('status', e.target.value)} className={selectClass}>
                {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Maaş</label>
              <CurrencyInput value={form.salary} onChange={v => update('salary', v)} placeholder="Maaş tutarı" className={inputClass} />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Ödeme Günü</label>
              <select value={form.salary_payment_day} onChange={e => update('salary_payment_day', parseInt(e.target.value))} className={selectClass}>
                {PAYMENT_DAYS.map(d => <option key={d} value={d}>Ayın {d}'i</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Acil Durum Kişisi</label>
              <input type="text" value={form.emergency_contact_name} onChange={e => update('emergency_contact_name', capitalizeWords(e.target.value))} className={inputClass} placeholder="Ad Soyad" />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Acil Durum Telefonu</label>
              <input type="tel" value={form.emergency_contact_phone} onChange={e => update('emergency_contact_phone', digitsOnly(e.target.value))} className={inputClass} placeholder="05XX XXX XX XX" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-foreground-600 mb-1.5">Notlar</label>
              <textarea value={form.notes} onChange={e => update('notes', e.target.value)} className={`${inputClass} min-h-[80px]`} placeholder="Personel hakkında notlar..." rows={3}></textarea>
            </div>
          </div>
        </form>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-background-200/70">
          <button type="button" onClick={onClose} className="btn-secondary cursor-pointer whitespace-nowrap">İptal</button>
          <button type="button" onClick={handleSubmit} className="btn-primary cursor-pointer whitespace-nowrap">{initialData ? 'Güncelle' : 'Kaydet'}</button>
        </div>
      </div>
    </div>
  );
}